import {register} from '../modules/eventMap';
import {kuiset} from '../utils/kuiset';
import {handleResponse} from '../modules/responseMixed';

const updateUI = (id) => (res, data) => {
    const progressBar = document.querySelector(
        `[kui-poll-progress-id="${id}"]`
    );
    const progressPercentDisplay = document.querySelector(
        `[kui-poll-progress-value-indicator]`
    );
    const progressPercentValue = Math.floor(res.progress);

    if (progressBar) {
        progressBar.value = progressPercentValue;
    }

    if (progressPercentDisplay) {
        progressPercentDisplay.innerText = `${progressPercentValue}% `;
    }

    return {ok: true, data};
};

const allowedUrls = [
    '/import/import_poll',
    '/business/export_poll',
    '/progress/delay',
    '/make/progress'
];

// Track active polling requests and controllers
const activePolls = new Map();

async function longPoll(url, data, callback, retryCount = 0, maxRetries = 5) {
    // Generate unique ID for this polling session
    const pollId = Math.random().toString(36).slice(2);

    // Create abort controller for this request
    const controller = new AbortController();
    activePolls.set(pollId, controller);

    const csrfToken = document.head.querySelector(
        '[name=csrf-token][content]'
    ).content;

    if (!allowedUrls.includes(url)) {
        return;
    }

    try {
        const response = await fetch(url, {
            method: 'POST',
            body: data,
            headers: {
                Accept: 'roast/mixed',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-Token': csrfToken
            },
            signal: controller.signal // Add abort signal
        });

        if (response.status !== 200) {
            if (retryCount < maxRetries) {
                if (response.status === 502) {
                    // Timeout error so try to reconnect
                    await longPoll(url, data, callback, retryCount + 1);
                } else {
                    console.error(response.statusText);
                    // General error. Reconnect in 1 second
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                    await longPoll(url, data, callback, retryCount + 1);
                }
            } else {
                console.error('Max retries exceeded');
            }
        } else {
            // Success
            const res = await handleResponse(response);
            if (res && res.action === 'polling') {
                const result = callback(res, data);
                if (result.ok) {
                    // Check again in 250ms
                    await new Promise((resolve) => setTimeout(resolve, 250));
                    // Only continue polling if not cancelled
                    if (activePolls.has(pollId)) {
                        await longPoll(url, result.data, callback);
                    }
                }
            }
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            console.log('Polling cancelled');
        } else {
            console.error(err);
        }
    } finally {
        // Clean up controller reference
        activePolls.delete(pollId);
    }

    return pollId;
}

// Add cancel method to longPoll
longPoll.cancel = (pollId) => {
    if (pollId) {
        // Cancel specific poll
        const controller = activePolls.get(pollId);
        if (controller) {
            controller.abort();
            activePolls.delete(pollId);
        }
    } else {
        // Cancel all active polls
        activePolls.forEach((controller) => controller.abort());
        activePolls.clear();
    }
};

const start = (el) => {
    const {pollUrl, pollProgressId} = kuiset(el);
    const formData = new FormData();

    Object.entries(kuiset(el)).forEach(([key, value]) => {
        if (key.includes('pollParam')) {
            formData.append(key.replace('pollParam', '').toLowerCase(), value);
        }
    });

    if (allowedUrls.includes(pollUrl)) {
        longPoll(pollUrl, formData, updateUI(pollProgressId));
    }
};

const poll = () => {
    register('poll', {
        start
    });
};

export {poll, longPoll};
